import { isReference } from '@apollo/client';
import type { FetchResult, StoreObject } from '@apollo/client';

import { isDevelopment } from '@constants/env';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import type { GetFhirRecordParams } from '@models/fhir-resource';
import { InsurancePlan } from '@models/insurance';

import { parseArgs, type CachedRecordList } from '@utils/apollo';

import {
  useListCoveragesQuery,
  usePostCoverageRecordMutation,
  useUpdateCoverageRecordMutation,
  useDeleteCoverageRecordMutation,
} from 'generated/insurance.generated';
import type {
  PostCoverageRecordMutationVariables,
  UpdateCoverageRecordMutationVariables,
  DeleteCoverageRecordMutation,
  DeleteCoverageRecordMutationVariables,
} from 'generated/insurance.generated';

export const useGetAllInsurance = (params?: { patient_id?: string }) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = params?.patient_id ?? activeProfile?.patient_id ?? '';
  const response = useListCoveragesQuery({
    variables: { input: { subscriber: patient_id, count: 1500, page: 1 } },
    skip: !patient_id,
  });
  const { data, error } = response;
  handleError(error);
  const plans = (data?.listCoverages.data ?? []).map(
    (r) => new InsurancePlan(r)
  );
  return { ...response, data: plans };
};

export const useGetInsurance = (params: GetFhirRecordParams) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const { record_id } = params;
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useListCoveragesQuery({
    variables: { input: { subscriber: patient_id, count: 1500, page: 1 } },
    skip:
      !patient_id || !record_id || record_id === 'add' || Boolean(params.skip),
  });
  const { data, error } = response;
  handleError(error);
  const plan = data?.listCoverages.data?.find(({ id }) => id === record_id);
  return { ...response, data: plan ? new InsurancePlan(plan) : null };
};

export function useCreateInsurance(): [
  (
    params: PostCoverageRecordMutationVariables
  ) => Promise<FetchResult<InsurancePlan>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = usePostCoverageRecordMutation();
  const loading = baseLoading;

  async function createInsurance(params: PostCoverageRecordMutationVariables) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.postCoverageRecord) {
          return;
        }
        const storeObject =
          response.postCoverageRecord as unknown as StoreObject;
        const coverage = params.input;
        try {
          cache.modify<{ listCoverages: CachedRecordList }>({
            fields: {
              listCoverages: (existing, { storeFieldName, toReference }) => {
                if (isReference(existing)) {
                  return existing;
                }
                const parsed = parseArgs(storeFieldName);
                if (parsed?.subscriber === coverage.subscriber?.reference) {
                  return {
                    ...existing,
                    data: [...existing.data, toReference(storeObject)],
                  };
                }
                return existing;
              },
            },
          });
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    const createdInsurance = response.data?.postCoverageRecord;
    return {
      ...response,
      data: createdInsurance ? new InsurancePlan(createdInsurance) : undefined,
    };
  }
  return [createInsurance, loading];
}

export function useUpdateInsurance(): [
  (
    params: UpdateCoverageRecordMutationVariables
  ) => Promise<FetchResult<InsurancePlan>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = useUpdateCoverageRecordMutation();
  const loading = baseLoading;

  async function updateInsurance(
    params: UpdateCoverageRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
    });
    const updatedInsurance = response.data?.updateCoverageRecord;
    return {
      ...response,
      data: updatedInsurance ? new InsurancePlan(updatedInsurance) : undefined,
    };
  }
  return [updateInsurance, loading];
}

export function useDeleteInsurance(): [
  (
    params: DeleteCoverageRecordMutationVariables
  ) => Promise<FetchResult<DeleteCoverageRecordMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = useDeleteCoverageRecordMutation();
  const loading = baseLoading;

  async function deleteInsurance(
    params: DeleteCoverageRecordMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.deleteCoverageRecord) {
          return;
        }
        const { coverage_id: id } = params.input;
        try {
          const normalizedId = cache.identify({
            id,
            __typename: 'CoverageModel',
          });
          cache.evict({ id: normalizedId });
          cache.gc();
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }
  return [deleteInsurance, loading];
}
